import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { IRootState } from 'config/redux';
import { ServiceConstants } from 'constant';

const { REQUEST_PENDING } = ServiceConstants;

interface UseServiceSuccessOptions {
  onSuccess?: () => void;
  enabled?: boolean;
}

/**
 * Hook to track Redux service status and trigger callback on PENDING → SUCCESS transition.
 * Solves the problem of Redux keeping SUCCESS state which causes unwanted re-triggers.
 * 
 * @param requestId - The Redux service request ID to track
 * @param options - Configuration options
 * @returns Object with loading state and data
 */
const useServiceSuccess = (
  requestId: string | undefined,
  options: UseServiceSuccessOptions = {}
) => {
  const { onSuccess, enabled = true } = options;
  
  const serviceObject = useSelector((state: IRootState) => 
    requestId ? state.service.object[requestId] : undefined
  );
  
  const prevStatus = useRef<string | undefined>();
  const onSuccessRef = useRef(onSuccess);
  
  // Keep callback ref updated
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    if (!enabled || !requestId) return;
    
    if (prevStatus.current === REQUEST_PENDING && serviceObject?.status === 'SUCCESS') {
      onSuccessRef.current?.();
    }
    prevStatus.current = serviceObject?.status;
  }, [serviceObject?.status, enabled, requestId]);

  return {
    isLoading: serviceObject?.status === REQUEST_PENDING,
    isSuccess: serviceObject?.status === 'SUCCESS',
    isError: serviceObject?.status === 'ERROR',
    data: serviceObject?.data,
    status: serviceObject?.status,
  };
};

export default useServiceSuccess;
